import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';

const ChangeTeamDialog = ({ open, onClose, onConfirm, user, loading }) => {
  const { getAccessToken } = useAuth();
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedTeam(null);
      setSearch('');
      fetchTeams();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchTeams = async () => {
    setTeamsLoading(true);
    setTeamsError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch teams: ${response.status}`);

      const result = await response.json();
      const data = result.data || result;
      setTeams(Array.isArray(data) ? data : []);
    } catch (err) {
      setTeamsError(err.message || 'Failed to load teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedTeam) return;
    onConfirm({
      userId: user.id,
      teamName: selectedTeam.name,
    });
  };

  const filteredTeams = teams.filter((t) =>
    !search.trim() || t.name.toLowerCase().includes(search.toLowerCase())
  );

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupIcon color="primary" />
        Change Team
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{user.iboName || 'Unknown'}</strong> (IBO: {user.iboNumber})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current team: {user.teamName || 'None'}
          </Typography>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Search teams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        {teamsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : teamsError ? (
          <Alert severity="error" sx={{ my: 1 }}>{teamsError}</Alert>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 280,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filteredTeams.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">No teams found</Typography>
              </Box>
            ) : (
              filteredTeams.map((team) => {
                const isCurrent = user.teamName === team.name;
                const isSelected = selectedTeam?.id === team.id;
                return (
                  <ListItemButton
                    key={team.id}
                    selected={isSelected}
                    onClick={() => setSelectedTeam(team)}
                    sx={{
                      borderRadius: 0.5,
                      mx: 0.5,
                      my: 0.25,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                            {team.name}
                          </Typography>
                          {isCurrent && (
                            <Chip label="current" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                          )}
                        </Box>
                      }
                      secondary={team.subdomain}
                    />
                    {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />}
                  </ListItemButton>
                );
              })
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !selectedTeam}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Updating...' : 'Change Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeTeamDialog;
